import React from 'react';
import {
  Droplets,
  Heart,
  Activity,
  Wind,
  Thermometer,
  Scale,
  Footprints,
  Moon,
  Dumbbell,
  Flame,
} from 'lucide-react';

export const VITAL_CONFIG = {
  'Blood glucose': {
    key: 'blood_glucose',
    unit: 'mg/dL',
    color: '#8b5cf6',
    domain: [60, 200],
    normalRange: { min: 70, max: 140 },
    tickCount: 8,
    label: 'Blood Glucose',
    icon: Droplets,
  },
  'Heart Rate': {
    key: 'heart_rate',
    unit: 'bpm',
    color: '#ef4444',
    domain: [40, 180],
    normalRange: { min: 60, max: 100 },
    tickCount: 8,
    label: 'Heart Rate',
    icon: Heart,
  },
  'Blood Pressure': {
    key: 'blood_pressure',
    systolicKey: 'systolic_bp',
    diastolicKey: 'diastolic_bp',
    unit: 'mmHg',
    systolicColor: '#ef4444',
    diastolicColor: '#3b82f6',
    domain: [40, 200],
    normalRange: {
      systolic: { min: 90, max: 120 },
      diastolic: { min: 60, max: 80 },
    },
    tickCount: 9,
    label: 'Blood Pressure',
    icon: Activity,
  },
  SpO2: {
    key: 'oxygen_saturation',
    unit: '%',
    color: '#06b6d4',
    domain: [85, 100],
    normalRange: { min: 95, max: 100 },
    tickCount: 6,
    label: 'SpO2',
    icon: Wind,
  },
  Temperature: {
    key: 'temperature',
    unit: '°C',
    color: '#f43f5e',
    domain: [34, 42],
    normalRange: { min: 36.1, max: 37.2 },
    tickCount: 6,
    label: 'Temperature',
    icon: Thermometer,
  },
  Weight: {
    key: 'weight_kg',
    unit: 'kg',
    color: '#f59e0b',
    domain: 'auto',
    normalRange: null,
    tickCount: 6,
    label: 'Weight',
    icon: Scale,
  },
  Steps: {
    key: 'steps',
    unit: 'steps',
    color: '#10b981',
    domain: [0, 'auto'],
    normalRange: { min: 10000, max: null },
    tickCount: 6,
    label: 'Daily Steps',
    icon: Footprints,
  },
  'Sleep Hours': {
    key: 'sleep_hours',
    unit: 'hrs',
    color: '#6366f1',
    domain: [0, 12],
    normalRange: { min: 7, max: 9 },
    tickCount: 7,
    label: 'Sleep Hours',
    icon: Moon,
  },
  Workouts: {
    key: 'workouts',
    unit: 'sessions',
    color: '#f97316',
    domain: [0, 'auto'],
    normalRange: { min: 3, max: null },
    tickCount: 6,
    label: 'Workouts',
    icon: Dumbbell,
  },
  Calories: {
    key: 'calories',
    unit: 'kcal',
    color: '#ea580c',
    domain: [0, 'auto'],
    normalRange: { min: 1800, max: 2500 },
    tickCount: 6,
    label: 'Calories',
    icon: Flame,
  },
};

export const THRESHOLD_METRICS = (() => {
  const list = [];
  Object.entries(VITAL_CONFIG).forEach(([displayName, cfg]) => {
    if (displayName === 'Blood Pressure') {
      list.push({
        value: cfg.systolicKey,
        label: 'Systolic Blood Pressure',
        unit: cfg.unit,
        icon: cfg.icon,
        defaultRange: cfg.normalRange?.systolic || null,
      });
      list.push({
        value: cfg.diastolicKey,
        label: 'Diastolic Blood Pressure',
        unit: cfg.unit,
        icon: cfg.icon,
        defaultRange: cfg.normalRange?.diastolic || null,
      });
      return;
    }
    list.push({
      value: cfg.key,
      label: cfg.label || displayName,
      unit: cfg.unit,
      icon: cfg.icon,
      defaultRange: cfg.normalRange || null,
    });
  });
  return list;
})();

export const getMetricByKey = (key) =>
  THRESHOLD_METRICS.find((m) => m.value === key) || null;

export function VitalIcon({ icon: Icon, className = 'w-4 h-4 inline-block', strokeWidth = 1.75 }) {
  if (!Icon) return null;
  if (typeof Icon === 'string') return <span className={className}>{Icon}</span>;
  return <Icon className={className} strokeWidth={strokeWidth} aria-hidden="true" />;
}
